<template>
  <section class="bank-connection-panel" aria-labelledby="bank-connection-heading">
    <div class="bank-connection-panel__header">
      <div>
        <h2 id="bank-connection-heading" class="bank-connection-panel__title">
          Operating bank
        </h2>
        <p class="bank-connection-panel__purpose">
          Connect this organization’s bank through Plaid so we can pull deposits
          and later match them to open invoices. Sign-in happens in Plaid, not
          in Praetorian.
        </p>
      </div>
      <PlaidLinkButton
        v-if="!items.length"
        use-case="BILLING"
        label="Connect bank"
        :redirect-uri="redirectUri"
        @success="onConnected"
        @error="onError"
      />
    </div>

    <p v-if="error" class="bank-connection-panel__error" role="alert">{{ error }}</p>
    <p v-if="loading && !items.length" class="bank-connection-panel__status">
      Checking bank connection…
    </p>

    <div v-for="item in items" :key="item.item_id" class="bank-connection-panel__item">
      <div class="bank-connection-panel__item-row">
        <div>
          <strong>{{ item.institution_name || 'Connected institution' }}</strong>
          <span class="bank-connection-panel__meta">
            {{ item.status }}
            <template v-if="item.last_sync_at"> · last sync {{ formatWhen(item.last_sync_at) }}</template>
          </span>
        </div>
        <div class="bank-connection-panel__actions">
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="syncingId === item.item_id"
            @click="syncItem(item)"
          >
            {{ syncingId === item.item_id ? 'Syncing…' : 'Sync bank data' }}
          </button>
        </div>
      </div>
      <ul class="bank-connection-panel__accounts">
        <li v-for="account in item.accounts" :key="account.id">
          <button
            type="button"
            class="bank-connection-panel__account"
            @click="loadTransactions(account)"
          >
            {{ account.name }} ···{{ account.mask }}
            <span v-if="account.current_balance != null">
              · {{ formatMoney(account.current_balance, account.balance_currency) }}
            </span>
          </button>
        </li>
      </ul>
    </div>

    <div v-if="selectedAccount" class="bank-connection-panel__txns">
      <h3>{{ selectedAccount.name }} recent activity</h3>
      <p v-if="txnLoading">Loading transactions…</p>
      <p v-else-if="!transactions.length">
        No transactions stored yet. Use Sync transactions — Plaid can take a
        minute after a new connection.
      </p>
      <table v-else class="bank-connection-panel__table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Description</th>
            <th class="right">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="txn in transactions" :key="txn.id">
            <td>{{ txn.date }}</td>
            <td>{{ txn.merchant_name || txn.name }}</td>
            <td class="right">
              {{ formatTransactionAmount(txn.amount, txn.currency) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="suggestions.length" class="bank-connection-panel__suggestions">
      <h3>Payments ready to review</h3>
      <p>
        These are suggestions only. Confirming marks the selected invoice paid.
      </p>
      <article
        v-for="suggestion in suggestions"
        :key="suggestion.id"
        class="bank-connection-panel__suggestion"
      >
        <div>
          <strong>
            {{ formatMoney(
              suggestion.transaction.amount,
              suggestion.transaction.currency,
            ) }}
            deposit
          </strong>
          <span class="bank-connection-panel__meta">
            {{ suggestion.transaction.date }} ·
            {{ suggestion.transaction.merchant_name || suggestion.transaction.name }}
          </span>
        </div>
        <div class="bank-connection-panel__match">
          <span>
            Likely {{ suggestion.invoice.invoice_number }} ·
            {{ suggestion.invoice.client_name }}
          </span>
          <small>{{ suggestion.reasons.join(' · ') }}</small>
        </div>
        <div class="bank-connection-panel__actions">
          <button
            type="button"
            class="btn btn-primary"
            :disabled="reviewingId === suggestion.id"
            @click="confirmSuggestion(suggestion)"
          >
            Confirm paid
          </button>
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="reviewingId === suggestion.id"
            @click="ignoreSuggestion(suggestion)"
          >
            Not a match
          </button>
        </div>
      </article>
    </div>
  </section>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import api from '@/services/api';
import PlaidLinkButton from '@/components/plaid/PlaidLinkButton.vue';

const emit = defineEmits(['invoice-paid']);
const items = ref([]);
const loading = ref(false);
const error = ref('');
const syncingId = ref(null);
const selectedAccount = ref(null);
const transactions = ref([]);
const txnLoading = ref(false);
const suggestions = ref([]);
const reviewingId = ref(null);

// Plaid rejects a non-HTTPS redirect_uri, so local dev omits it and uses the
// non-OAuth Link flow.
const redirectUri =
  typeof window !== 'undefined' && window.location.protocol === 'https:'
    ? window.location.origin
    : null;

async function loadAccounts() {
  loading.value = true;
  error.value = '';
  try {
    const response = await api.get('/users/plaid/accounts/', {
      params: { use_case: 'BILLING' },
    });
    items.value = Array.isArray(response.data) ? response.data : [];
  } catch (err) {
    error.value = err.response?.data?.error || 'Could not load bank connections.';
  } finally {
    loading.value = false;
  }
}

async function loadSuggestions() {
  try {
    const response = await api.get('/invoices/plaid-payment-suggestions/');
    suggestions.value = Array.isArray(response.data) ? response.data : [];
  } catch (err) {
    error.value =
      err.response?.data?.detail ||
      err.response?.data?.error ||
      'Could not load payment suggestions.';
  }
}

async function onConnected() {
  await loadAccounts();
  await loadSuggestions();
}

function onError(payload) {
  error.value = payload?.error || 'Plaid Link failed.';
}

async function syncItem(item) {
  syncingId.value = item.item_id;
  error.value = '';
  try {
    const response = await api.post(`/users/plaid/items/${item.item_id}/sync/`);
    if (response.data?.balance_error) {
      error.value = response.data.balance_error;
    }
    await loadAccounts();
    if (selectedAccount.value) {
      await loadTransactions(selectedAccount.value);
    }
    await loadSuggestions();
  } catch (err) {
    error.value = err.response?.data?.error || 'Transaction sync failed.';
  } finally {
    syncingId.value = null;
  }
}

async function confirmSuggestion(suggestion) {
  reviewingId.value = suggestion.id;
  error.value = '';
  try {
    await api.post(
      `/invoices/plaid-payment-suggestions/${suggestion.id}/confirm/`,
    );
    emit('invoice-paid', suggestion.invoice.id);
    await loadSuggestions();
  } catch (err) {
    if (err.response?.status === 409) {
      error.value =
        'That invoice is no longer open. Suggestions have been refreshed.';
      await loadSuggestions();
    } else {
      error.value =
        err.response?.data?.detail || 'Could not confirm this payment.';
    }
  } finally {
    reviewingId.value = null;
  }
}

async function ignoreSuggestion(suggestion) {
  reviewingId.value = suggestion.id;
  error.value = '';
  try {
    await api.post(
      `/invoices/plaid-payment-suggestions/${suggestion.id}/ignore/`,
    );
    await loadSuggestions();
  } catch (err) {
    error.value =
      err.response?.data?.detail || 'Could not dismiss this suggestion.';
  } finally {
    reviewingId.value = null;
  }
}

async function loadTransactions(account) {
  selectedAccount.value = account;
  txnLoading.value = true;
  error.value = '';
  try {
    const response = await api.get(
      `/users/plaid/accounts/${account.id}/transactions/`,
      { params: { limit: 25, pending: true } },
    );
    transactions.value = response.data?.transactions || [];
  } catch (err) {
    error.value = err.response?.data?.error || 'Could not load transactions.';
    transactions.value = [];
  } finally {
    txnLoading.value = false;
  }
}

function formatMoney(amount, currency) {
  const n = Number(amount);
  if (Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
  }).format(n);
}

function formatTransactionAmount(amount, currency) {
  const n = Number(amount);
  if (Number.isNaN(n)) return '—';
  const direction = n < 0 ? '+' : '−';
  return `${direction}${formatMoney(Math.abs(n), currency)}`;
}

function formatWhen(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

onMounted(async () => {
  await loadAccounts();
  if (items.value.length) {
    await loadSuggestions();
  }
});
</script>

<style scoped>
.bank-connection-panel {
  margin: 0 0 1.5rem;
  padding: 1rem 1.25rem;
  border: 1px solid var(--border, #e0e4f0);
  border-radius: 10px;
  background: #fff;
}
.bank-connection-panel__header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
}
.bank-connection-panel__title {
  margin: 0 0 0.35rem;
  font-size: 1.05rem;
}
.bank-connection-panel__purpose {
  margin: 0;
  max-width: 42rem;
  color: var(--text-secondary, #6c7a9c);
  font-size: 0.875rem;
}
.bank-connection-panel__error {
  color: #c62828;
  margin: 0.75rem 0 0;
}
.bank-connection-panel__status {
  margin: 0.75rem 0 0;
  color: var(--text-secondary, #6c7a9c);
}
.bank-connection-panel__item {
  margin-top: 1rem;
}
.bank-connection-panel__item-row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
}
.bank-connection-panel__meta {
  display: block;
  font-size: 0.8rem;
  color: var(--text-secondary, #6c7a9c);
  font-weight: 400;
}
.bank-connection-panel__accounts {
  list-style: none;
  margin: 0.5rem 0 0;
  padding: 0;
}
.bank-connection-panel__account {
  background: none;
  border: none;
  padding: 0.25rem 0;
  cursor: pointer;
  color: var(--primary, #2c3e8c);
  font-size: 0.875rem;
}
.bank-connection-panel__txns {
  margin-top: 1rem;
}
.bank-connection-panel__txns h3 {
  margin: 0 0 0.5rem;
  font-size: 0.95rem;
}
.bank-connection-panel__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}
.bank-connection-panel__table th,
.bank-connection-panel__table td {
  text-align: left;
  padding: 0.4rem 0.5rem;
  border-bottom: 1px solid var(--border, #e0e4f0);
}
.bank-connection-panel__table .right {
  text-align: right;
}
.bank-connection-panel__suggestions {
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border, #e0e4f0);
}
.bank-connection-panel__suggestions h3 {
  margin: 0;
  font-size: 0.95rem;
}
.bank-connection-panel__suggestions > p {
  margin: 0.25rem 0 0.75rem;
  color: var(--text-secondary, #6c7a9c);
  font-size: 0.8rem;
}
.bank-connection-panel__suggestion {
  display: grid;
  grid-template-columns: minmax(10rem, 1fr) minmax(14rem, 1.5fr) auto;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 0;
  border-top: 1px solid var(--border, #e0e4f0);
}
.bank-connection-panel__match {
  display: flex;
  flex-direction: column;
  font-size: 0.875rem;
}
.bank-connection-panel__match small {
  color: var(--text-secondary, #6c7a9c);
}
.bank-connection-panel__actions {
  display: flex;
  gap: 0.5rem;
}
@media (max-width: 767px) {
  .bank-connection-panel__header,
  .bank-connection-panel__item-row,
  .bank-connection-panel__suggestion {
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
